import React from 'react';
import { Box, useTheme, Tooltip, useMediaQuery } from '@mui/material';
import { Typography } from '@sm-dashboard/ui-components';
import { formStyles } from '@/components/common/SharedFormComponents';

interface CrmMetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  bgColor: string;
  secondaryText?: string;
  onClick?: () => void;
  tooltip?: string;
}

const CrmMetricCard = ({ icon, label, value, bgColor, secondaryText, onClick, tooltip }: CrmMetricCardProps) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));
  const formattedValue = typeof value === 'number' ? value.toLocaleString('he-IL') : value;

  const cardContent = (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: bgColor,
        borderRadius: 1,
        p: isCompact ? 1 : 2,
        width: '100%',
        minWidth: 0,
        height: isCompact ? 100 : 150,
        boxShadow: formStyles.statisticsCard.card.boxShadow,
        transition: 'transform 0.2s ease-in-out',
        cursor: onClick ? 'pointer' : 'default',
        alignItems: isCompact ? 'center' : 'stretch',
        justifyContent: isCompact ? 'center' : 'flex-start',
        position: 'relative',
        '&:hover': {
          transform: isCompact ? 'none' : 'translateY(-4px)',
          boxShadow: isCompact ? formStyles.statisticsCard.card.boxShadow : '0 8px 20px rgba(0,0,0,0.2)'
        }
      }}
    >
      {isCompact ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            width: '100%',
            minWidth: 0,
            flex: 1
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'common.white',
              lineHeight: 0,
              mb: 0.25,
              '& svg': {
                fontSize: 22
              }
            }}
          >
            {icon}
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              width: '100%',
              px: 0.25,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            <Typography variant="caption" color="primary.main">
              {label}
            </Typography>
          </Box>
          <Box
            sx={{
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 0.25
            }}
          >
            <Typography variant="h6" color="primary.main">
              {formattedValue}
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'common.white',
              '& svg': {
                fontSize: 40
              }
            }}
          >
            {icon}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              height: '100%',
              gap: 0.5,
              pb: 1,
              minWidth: 0,
              width: '100%'
            }}
          >
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2" color="primary.main">
                {label}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h1" color="primary.main">
                {formattedValue}
              </Typography>
            </Box>
            {secondaryText && (
              <Box sx={{ textAlign: 'right', opacity: 0.8 }}>
                <Typography variant="subtitle1" color="primary.main">
                  {secondaryText}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow placement="top">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

export default CrmMetricCard;
